"use client";
import { getAuthMenuByWorkspace } from "@/data/memo-menu-auth-data";
import { Routes } from "@/constants";
import { useTranslations } from "next-intl";
import Link from "next/link";
import React, { useContext, useState } from "react";
import { useRouter } from "next/navigation";
import { AppContext } from "@/contextApi/AppProvider";
import { AppContextType, WorkspaceType } from "@/interFace/general";
import { getLink } from "@/constants";
import { Translations } from "@/translations";

const MAX_MENU_PROFILE_LABEL_LENGTH = 10;

const truncateMenuProfileLabel = (label: string) => {
  if (label.length <= MAX_MENU_PROFILE_LABEL_LENGTH) {
    return label;
  }

  return `${label.slice(0, MAX_MENU_PROFILE_LABEL_LENGTH - 1)}...`;
};

const MemoAuthMenuTwo = () => {
  const [submenuOpen, setSubmenuOpen] = useState<number>(0);
  const [open, setOpen] = useState<boolean>(false);
  const tMenu = useTranslations("Menu");
  const tInbox = useTranslations("Inbox");
  const router = useRouter();

  const { toggleSideMenu, sideMenuOpen, currentUser, userInfo, setUserInfo, locale, unreadCount, invitationCount, bookingNotificationCount, contactCount } = useContext(
    AppContext
  ) as AppContextType;
  const menuData = getAuthMenuByWorkspace(userInfo.activeWorkspace, userInfo.activeRole, userInfo.ageGroup);
  const profileLabel =
    currentUser?.displayName?.trim() ||
    currentUser?.providerData?.find((provider: { displayName?: string | null }) => provider?.displayName?.trim())?.displayName?.trim() ||
    currentUser?.email?.split("@")[0] ||
    tMenu("Profile");
  const menuProfileLabel = truncateMenuProfileLabel(profileLabel);

  const handleMenuToggle = (id: number) => {
    setSubmenuOpen(id);
    setOpen(!open);
  };

  const translateLabel = (
    title: string,
    namespace?: "Menu" | "Inbox"
  ) => {
    return namespace === "Inbox" ? tInbox(title) : tMenu(title);
  };

  const getWorkspaceSwitchTitle = (workspace: WorkspaceType) => {
    if (workspace === "claimant") {
      return tMenu(Translations.Menu.SwitchToClaimantWorkspace);
    }

    if (workspace === "tutor") {
      return tMenu(Translations.Menu.SwitchToTutorWorkspace);
    }

    if (workspace === "parent") {
      return tMenu(Translations.Menu.SwitchToParentWorkspace);
    }

    return tMenu(Translations.Menu.SwitchToStudentWorkspace);
  };

  const getWorkspaceLandingUrl = (workspace: WorkspaceType) => {
    if (workspace === "claimant") {
      return getLink(locale, Routes.CLAIMANT_ROOT);
    }

    if (workspace === "tutor") {
      return getLink(locale, Routes.TUTORS_ROOT);
    }

    if (workspace === "parent") {
      return getLink(locale, Routes.PARENTS_ROOT);
    }

    return getLink(locale, Routes.DASHBOARD_COURSES);
  };

  const getWorkspaceRole = (workspace: WorkspaceType) => {
    if (workspace === "claimant") {
      return "claimant";
    }

    if (workspace === "tutor") {
      return "tutor";
    }

    if (workspace === "parent") {
      return "parent";
    }

    return "student";
  };

  const switchTargetWorkspaces = userInfo.workspaces.filter((workspace) => {
    return workspace !== userInfo.activeWorkspace;
  });

  const handleWorkspaceSwitch = (workspace: WorkspaceType) => {
    setUserInfo((previous) => ({
      ...previous,
      activeWorkspace: workspace,
      activeRole: getWorkspaceRole(workspace),
    }));

    toggleSideMenu?.();
    router.push(getWorkspaceLandingUrl(workspace));
  };

  return (
    <>
      {menuData?.length &&
        menuData?.map((item) => (
          <li
            onClick={() => handleMenuToggle(item.id)}
            key={item.id}
            className={`${
              item.hasDropdown && submenuOpen === item.id && open === true
                ? "menu-item-has-children has-droupdown active"
                : `${
                    item.hasDropdown
                      ? "menu-item-has-children has-droupdown"
                      : ""
                  }`
            }`}
          >
            {item.hasDropdown === true ? (
              <>
                <div
                  className="submenu active"
                  style={{
                    display: "block",
                  }}
                >
                  <ul>
                    {item?.submenus?.length &&
                      item?.submenus.map((data, index) => (
                        <li key={index}>
                          <Link
                            onClick={toggleSideMenu}
                            className="text-decoration-none"
                            href={data.link}
                          >
                            {" "}
                            {translateLabel(data.title, data.translationNamespace)}
                            {data.link === Routes.INBOX && unreadCount > 0 && (
                              <span className="badge bg-danger rounded-pill ms-1">{unreadCount}</span>
                            )}
                            {data.link === Routes.FRIENDS && invitationCount > 0 && (
                              <span className="badge bg-warning text-dark rounded-pill ms-1">{invitationCount}</span>
                            )}
                            {data.link === Routes.CONTACTS && contactCount > 0 && (
                              <span className="badge bg-success rounded-pill ms-1">{contactCount}</span>
                            )}
                          </Link>
                        </li>
                      ))}
                    {item.isProfile && switchTargetWorkspaces.map((workspace) => (
                      <li key={workspace}>
                        <button
                          type="button"
                          onClick={() => handleWorkspaceSwitch(workspace)}
                          className="text-decoration-none"
                        >
                          {getWorkspaceSwitchTitle(workspace)}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              </>
            ) : (
                <Link
                  onClick={toggleSideMenu}
                  className="text-decoration-none"
                  href={getLink(locale, item.link)}
                  title={item.title === "PROFILE_NAME" ? profileLabel : item.iconClass ? translateLabel(item.title, item.translationNamespace) : undefined}
                >
                {" "}
                {item.iconClass ? (
                  <>
                    <i className={item.iconClass} aria-hidden="true"></i>
                    <span className="visually-hidden">{translateLabel(item.title, item.translationNamespace)}</span>
                  </>
                ) : item.title && item.title !== "PROFILE_NAME"
                  ? translateLabel(item.title, item.translationNamespace)
                  : ""}
                {item.isBookings && bookingNotificationCount > 0 && (
                  <span className="badge bg-danger rounded-pill ms-1" aria-label="Pending booking notifications">
                    <i className="bi bi-bell-fill"></i>
                  </span>
                )}
                {item.title && item.title === "PROFILE_NAME"
                  ? menuProfileLabel
                  : ""}
              </Link>
            )}
          </li>
        ))}
    </>
  );
};

export default MemoAuthMenuTwo;

// const menuWithSubmenu = (
//   <>
//   <button className="text-decoration-none menu-link">
//     {" "}
//     {t(item.title)}{" "}
//   </button>
//   <ul
//     className={
//       item.submenus && submenuOpen === item.id && open === true
//         ? "submenu active"
//         : "submenu"
//     }
//     style={{
//       display:
//         item.submenus && submenuOpen === item.id && open === true
//           ? "block"
//           : "none",
//     }}
//   >
//     {item?.submenus?.length &&
//       item?.submenus.map((data, index) => (
//         <li key={index}>
//           <Link
//             onClick={toggleSideMenu}
//             className="text-decoration-none"
//             href={data.link}
//           >
//             {" "}
//             {data.title}{" "}
//           </Link>
//         </li>
//       ))}
//   </ul>
// </>
// )
